import { Box, Divider, Grid, IconButton, Stack, Tooltip } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CustomText from 'components/common/CustomText/CustomText'
import { colors } from 'styles/colors'
import useGlobalStyles from "styles/globalStyles";
import { memo, ReactNode, useState } from 'react'
import { SectionButtonType } from 'types/types';
import TitleText from '../CustomText/TitleText';

interface Props {
  title: string;
  buttons?: SectionButtonType[];
  content?: ReactNode;
  carousel?: ReactNode;
  contentPadding?: number;
  onTitlePress?: () => void;
  titleHint?: string;
  // A word on how much the section holds, shown beside its title: with the
  // longer sections folded away, it is what says whether there is anything
  // behind the fold.
  subtitle?: string;
  // Pressing the heading folds the section away and pressing it again brings it
  // back. It was an eye among the buttons before, which put the one thing done
  // to every section in the row where each section keeps the things done only
  // to it, and left the largest target on the heading doing nothing.
  //
  // Every section folds, and one that says nothing about it folds on a state of
  // its own: a fold is a thing done to a heading rather than a feature a
  // section has to be given. A section that does keep the state - because its
  // content is built only when it is open, or because it must stay open while
  // an edit is in it - passes both, and passes no onToggle while the fold is to
  // be refused.
  collapsed?: boolean;
  onToggle?: () => void;
}

function Section({
  title,
  buttons = [],
  content,
  carousel,
  contentPadding = 3,
  onTitlePress,
  titleHint = '',
  subtitle,
  collapsed,
  onToggle,
}: Props) {
  const globalStyles = useGlobalStyles();
  const [foldedHere, setFoldedHere] = useState(false);

  // Open unless told otherwise, and folded by whoever is keeping the state.
  const held = collapsed !== undefined;
  const folded = held ? collapsed : foldedHere;
  const toggle = held ? onToggle : () => setFoldedHere((shown) => !shown);

  return (
    <Box className={globalStyles.sectionContainer}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        minHeight={80}
        px={2}
        onClick={toggle}
        sx={toggle ? { cursor: 'pointer', userSelect: 'none' } : undefined}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          {/* Which way the section is, said by the one thing on the heading
              that is not a button: a heading that can be pressed and shows
              nothing for it is a heading nobody presses. */}
          {toggle && (
            <ExpandMoreIcon
              sx={{
                color: colors.gray,
                transition: 'transform 150ms',
                transform: folded ? 'rotate(-90deg)' : 'none',
              }}
            />
          )}
          {onTitlePress ? <TitleText
            title={titleHint}
            text={title}
            fontWeight={600}
            onClick={onTitlePress}
          /> : <CustomText
            text={title}
            color={colors.primary}
            fontWeight={600}
          />}

          {subtitle && (
            <CustomText text={subtitle} fontSize={13} color={colors.gray} />
          )}
        </Stack>

        {/* A button does what it says and nothing else: pressed here, it would
            also fold the section it was meant to act on. */}
        <Stack direction="row" gap={1} onClick={(event) => event.stopPropagation()}>
          {buttons.map((btn, index) => (
            <Box display="flex" key={index}>
              <Tooltip title={btn.title}>
                <IconButton
                  loading={btn.loading}
                  disabled={btn.disabled}
                  onClick={btn?.onClick}
                  sx={{
                    backgroundColor: colors.topBackground,
                    width: 35,
                    height: 35,
                    borderRadius: '4px',
                  }}
                >
                  {!btn.loading && btn.icon}
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      </Stack>

      {/* The line under the heading separates it from what it heads, so a
          folded section has nothing for it to separate. */}
      {!folded && <Divider />}

      {!folded && content && <Grid container p={contentPadding} spacing={1.5}>
        {content}
      </Grid>}

      {!folded && carousel && <Box p={contentPadding}>
        {carousel}
      </Box>}
    </Box>
  )
}

export default memo(Section);